import React, { useEffect, useState } from "react";
import { api, formatError } from "@/api/client";
import { Plus, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MODULES = [
  { key: "finance", label: "Finance" },
  { key: "work_orders", label: "Work Orders" },
  { key: "invoices", label: "Invoices" },
];

const TYPES = ["text", "number", "date", "select", "checkbox", "textarea"];

export default function CustomFieldsManager() {
  const [module, setModule] = useState("finance");
  const [fields, setFields] = useState([]);
  const [draft, setDraft] = useState({ name: "", label: "", type: "text", options: "", required: false });

  const load = () => api.get(`/custom-fields?module=${module}`).then((r) => setFields(r.data));
  useEffect(() => { load(); }, [module]);

  const add = async (e) => {
    e.preventDefault();
    if (!draft.label.trim()) return;
    const name = draft.name.trim() || draft.label.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    try {
      await api.post("/custom-fields", {
        module, name, label: draft.label, type: draft.type,
        options: draft.type === "select" ? draft.options.split(",").map((s) => s.trim()).filter(Boolean) : [],
        required: draft.required, order: fields.length,
      });
      setDraft({ name: "", label: "", type: "text", options: "", required: false });
      load(); toast.success("Field added");
    } catch (err) { toast.error(formatError(err)); }
  };

  const del = async (id) => { await api.delete(`/custom-fields/${id}`); load(); };

  return (
    <div className="dos-card p-6">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-3">Custom Fields</div>
      <div className="flex items-center gap-2 mb-4">
        <label className="text-xs text-neutral-500">Module:</label>
        <select data-testid="cf-module" value={module} onChange={(e) => setModule(e.target.value)} className="dos-input max-w-xs">
          {MODULES.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
        </select>
        {fields.length > 0 && (
          <button data-testid="cf-clear-module" onClick={async () => {
            if (!window.confirm(`Delete ALL ${fields.length} custom field${fields.length !== 1 ? "s" : ""} for "${module}"?`)) return;
            try { await api.post("/custom-fields/clear", { module }); toast.success("Cleared"); load(); }
            catch (err) { toast.error(formatError(err)); }
          }} className="dos-btn dos-btn-secondary text-xs text-red-600 ml-auto"><Trash size={12} /> Clear all fields for this module</button>
        )}
      </div>

      <div className="border border-neutral-200 rounded-md overflow-hidden mb-4">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Name</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Options</th>
              <th className="text-left px-3 py-2">Required</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f.id} className="border-t border-neutral-100">
                <td className="px-3 py-2">{f.label}</td>
                <td className="px-3 py-2 font-mono text-xs text-neutral-600">{f.name}</td>
                <td className="px-3 py-2"><span className="dos-badge bg-neutral-100 text-neutral-600">{f.type}</span></td>
                <td className="px-3 py-2 text-neutral-500 text-xs">{(f.options || []).join(", ") || "—"}</td>
                <td className="px-3 py-2 text-xs">{f.required ? "Yes" : "No"}</td>
                <td className="px-3 py-2 text-right"><button data-testid={`cf-del-${f.id}`} onClick={() => del(f.id)} className="p-1 rounded hover:bg-neutral-100 text-neutral-400 hover:text-red-500"><Trash size={14} /></button></td>
              </tr>
            ))}
            {fields.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-neutral-500">No custom fields for this module yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <form onSubmit={add} className="grid grid-cols-6 gap-2 items-end">
        <div className="col-span-2">
          <label className="text-xs text-neutral-500">Label</label>
          <input data-testid="cf-label" required value={draft.label} onChange={(e) => setDraft({ ...draft, label: e.target.value })} className="dos-input mt-1" placeholder="e.g., RERA Number" />
        </div>
        <div>
          <label className="text-xs text-neutral-500">Type</label>
          <select data-testid="cf-type" value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value })} className="dos-input mt-1">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        {draft.type === "select" && (
          <div className="col-span-2">
            <label className="text-xs text-neutral-500">Options (comma separated)</label>
            <input value={draft.options} onChange={(e) => setDraft({ ...draft, options: e.target.value })} className="dos-input mt-1" placeholder="A, B, C" />
          </div>
        )}
        <label className="text-xs text-neutral-500 flex items-center gap-1 pb-2">
          <input type="checkbox" checked={draft.required} onChange={(e) => setDraft({ ...draft, required: e.target.checked })} /> Required
        </label>
        <button data-testid="cf-add" type="submit" className="dos-btn dos-btn-primary"><Plus size={14} /> Add</button>
      </form>
    </div>
  );
}

// Reusable renderer for custom fields in forms
export function CustomFieldsInputs({ module, values, onChange }) {
  const [fields, setFields] = useState([]);
  useEffect(() => {
    api.get(`/custom-fields?module=${module}`).then((r) => setFields(r.data)).catch(() => {});
  }, [module]);

  if (fields.length === 0) return null;

  const setVal = (name, v) => onChange({ ...values, [name]: v });

  return (
    <div className="space-y-3 pt-3 mt-3 border-t border-neutral-200">
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Custom Fields</div>
      {fields.map((f) => (
        <div key={f.id}>
          <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1 block">{f.label}{f.required && <span className="text-red-500">*</span>}</label>
          {f.type === "textarea" ? (
            <textarea required={f.required} value={values?.[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} className="dos-input" rows={2} />
          ) : f.type === "select" ? (
            <select required={f.required} value={values?.[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} className="dos-input">
              <option value="">Select…</option>
              {(f.options || []).map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : f.type === "checkbox" ? (
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!values?.[f.name]} onChange={(e) => setVal(f.name, e.target.checked)} /> {f.label}</label>
          ) : (
            <input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"} required={f.required} value={values?.[f.name] || ""} onChange={(e) => setVal(f.name, e.target.value)} className="dos-input" />
          )}
        </div>
      ))}
    </div>
  );
}
